import { Send, Sparkles } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";

import { aiChat } from "@/services/ai";

interface Message {
  role: "user" | "assistant";
  content: string;
  source?: string;
}

interface AIChatPanelProps {
  context?: string;
  onApplyCode?: (code: string) => void;
}

const suggestions = [
  "为当前文件生成单元测试",
  "把这段代码改写成 TypeScript",
  "解释这个组件的工作原理",
  "生成一个客户管理页面的 Schema",
];

export function AIChatPanel({ context, onApplyCode }: AIChatPanelProps) {
  const [messages, setMessages] = React.useState<Message[]>([
    {
      role: "assistant",
      content: "你好，我是罢歌亭 AI 副驾驶。你可以让我生成代码、解释逻辑或修改当前文件。",
    },
  ]);
  const [input, setInput] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const bottomRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async (text: string) => {
    if (!text.trim()) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setLoading(true);

    try {
      const prompt = context ? `当前文件内容：\n\`\`\`\n${context}\n\`\`\`\n\n需求：${text}` : text;
      const result = await aiChat(prompt, "dev-assistant");
      const response = result.response;
      const content =
        typeof response.content === "string"
          ? response.content
          : JSON.stringify(response, null, 2);
      setMessages((prev) => [...prev, { role: "assistant", content, source: result.source }]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: `请求失败：${String(error)}`, source: "error" },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const extractCode = (content: string) => {
    const match = /```(?:\w+)?\n([\s\S]*?)```/.exec(content);
    return match?.[1]?.trim();
  };

  return (
    <Card className="flex h-full flex-col">
      <CardHeader className="border-b pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Sparkles className="h-5 w-5 text-primary" />
          AI 副驾驶
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 space-y-3 overflow-auto py-3">
        {messages.map((msg, idx) => (
          <div key={idx} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[90%] whitespace-pre-wrap rounded-lg px-3 py-2 text-sm ${
                msg.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted"
              }`}
            >
              {msg.content}
              {msg.role === "assistant" && onApplyCode && extractCode(msg.content) && (
                <div className="mt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    onClick={() => onApplyCode(extractCode(msg.content)!)}
                  >
                    应用代码到编辑器
                  </Button>
                </div>
              )}
              {msg.source && <div className="mt-1 text-[10px] opacity-60">来源：{msg.source}</div>}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="max-w-[90%] rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">思考中...</div>
          </div>
        )}
        <div ref={bottomRef} />
      </CardContent>
      <div className="border-t bg-card p-3">
        <div className="mb-2 flex flex-wrap gap-1">
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => handleSend(s)}
              className="rounded-full border px-2 py-0.5 text-[10px] text-muted-foreground hover:border-primary hover:text-primary"
            >
              {s}
            </button>
          ))}
        </div>
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSend(input);
          }}
        >
          <Textarea
            className="min-h-[48px] flex-1 resize-none"
            placeholder="描述你的需求..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void handleSend(input);
              }
            }}
          />
          <Button type="submit" size="icon" disabled={loading || !input.trim()}>
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </div>
    </Card>
  );
}
